import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Star, Quote, Phone, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { API } from '../lib/api';



const Reviews = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        setTestimonials(response.data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      }
      setLoading(false);
    };
    fetchTestimonials();
  }, []);

  const stats = [
    { value: '500+', label: 'Projects Completed' },
    { value: '15+', label: 'Years Experience' },
    { value: '100%', label: 'Client Satisfaction' },
    { value: '50+', label: 'Active Clients' },
  ];

  return (
    <div data-testid="reviews-page">
      <Helmet>
        <title>Client Reviews & Testimonials | S&S Group Services | Vernon BC Contractor</title>
        <meta name="description" content="Read what our clients say about S&S Group Services. 500+ projects completed, 15+ years experience. 5-star rated construction company in Vernon, BC." />
        <meta name="keywords" content="S&S Group Services reviews, Vernon BC contractor reviews, construction company testimonials BC, best contractor Vernon" />
        <link rel="canonical" href="https://www.ssgroupservices.ca/reviews" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.ssgroupservices.ca/reviews" />
        <meta property="og:title" content="Client Reviews | S&S Group Services Vernon BC" />
        <meta property="og:description" content="5-star rated construction company. 500+ projects completed, 15+ years experience in BC." />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Client Reviews | S&S Group Services Vernon BC" />
      </Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.ssgroupservices.ca/" },
          { "@type": "ListItem", "position": 2, "name": "Reviews", "item": "https://www.ssgroupservices.ca/reviews" }
        ]
      }) }} />
      {/* Hero Section */}
      <section className="relative py-24 bg-slate-900" data-testid="reviews-hero">
        <div className="absolute inset-0 grid-pattern" />
        <div className="container relative z-10">
          <div className="max-w-3xl">
            <span className="caption text-blue-400">Client Testimonials</span>
            <h1 className="heading-xl text-white mt-3 mb-6">What Our Clients Say</h1>
            <p className="text-slate-300 text-lg">
              Don't just take our word for it. Here's what property developers, homeowners, and contractors have to say about working with S&S Group Services.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-blue-600" data-testid="stats-section">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>
                  {stat.value}
                </p>
                <p className="text-blue-100 text-sm uppercase tracking-wider">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews Grid */}
      <section className="section-padding bg-white" data-testid="reviews-grid">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="heading-lg text-slate-900 mb-4">Client Reviews</h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We take pride in our work and the relationships we build with our clients. Here are some of their experiences.
            </p>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {testimonials.map((testimonial, index) => (
                <div 
                  key={testimonial.id}
                  className="bg-white border border-slate-200 p-8 hover:shadow-lg transition-shadow"
                  data-testid={`review-card-${testimonial.id}`}
                >
                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>

                  {/* Quote Icon */}
                  <Quote className="w-10 h-10 text-blue-600/20 mb-4" />

                  {/* Review Text */}
                  <p className="text-slate-700 mb-6 leading-relaxed">"{testimonial.text}"</p>

                  {/* Author */}
                  <div className="flex items-center gap-4">
                    {testimonial.image && (
                      <img 
                        src={testimonial.image} 
                        alt={testimonial.name}
                        className="w-14 h-14 rounded-full object-cover"
                      />
                    )}
                    <div>
                      <p className="font-bold text-slate-900" style={{ fontFamily: 'Oswald, sans-serif' }}>
                        {testimonial.name}
                      </p>
                      <p className="text-slate-500 text-sm">{testimonial.role}</p>
                      {testimonial.company && (
                        <p className="text-blue-600 text-sm">{testimonial.company}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-padding bg-slate-50" data-testid="trust-section">
        <div className="container">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="caption text-blue-600">Why Clients Trust Us</span>
              <h2 className="heading-lg text-slate-900 mt-3 mb-6">Building Relationships, Not Just Buildings</h2>
              <p className="text-slate-600 mb-8">
                Our reputation is built on trust, transparency, and delivering exceptional results. We treat every project—big or small—with the same level of dedication and professionalism.
              </p>

              <ul className="space-y-4">
                {[
                  'Clear communication throughout every project',
                  'Transparent pricing with no hidden fees',
                  'Licensed and fully insured for your protection',
                  'Dedicated project managers for each job',
                  'Quality craftsmanship guaranteed',
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3" data-testid={`trust-item-${index}`}>
                    <CheckCircle2 className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1621831337120-95dfd510b4f8?w=600&h=500&fit=crop"
                alt="Construction team"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-slate-900 text-white p-8">
                <div className="flex items-center gap-2 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-2xl font-bold" style={{ fontFamily: 'Oswald, sans-serif' }}>5.0 Rating</p>
                <p className="text-slate-400 text-sm">Based on client reviews</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-slate-900" data-testid="reviews-cta">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="heading-lg text-white mb-6">Ready to Experience the S&S Difference?</h2>
            <p className="text-slate-300 text-lg mb-8">
              Join our growing list of satisfied clients. Contact us today to discuss your construction project.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button size="lg" className="btn-accent" data-testid="reviews-cta-quote-btn">
                  Get a Free Quote
                </Button>
              </Link>
              <a href="tel:+12363380659">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900" data-testid="reviews-cta-call-btn">
                  <Phone className="w-5 h-5 mr-2" />
                  (236) 338-0659
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
